import dynamic from "next/dynamic";
// Bound here, not only where an icon is drawn: every Client below is its own chunk, and a module they
// share without this file holding it is copied into each of them. The icon registry and its renderer
// were in five chunks of a contact page and six of a dashboard. Held by the manifest, they load once.
import "../../../components/shell/phi-icon";
import type { PhiRuntimeModuleControllerClient } from "../../../components/runtime/runtime-module-controller-client-manifest";
import {
  createPhiRuntimeModuleRenderClientManifest,
  definePhiRuntimeModuleRenderClient,
} from "../../../components/runtime/runtime-module-render-client-manifest";
import { PhiCmsWidgetType } from "../../../constants/cms-widget-types";
import { PhiCmsLayoutType } from "../../../constants/cms-layout-type-keys";
import { PhiRuntimeRenderClientType } from "../../../constants/runtime-render-client-types";
import { PHI_ASSET_RUNTIME_MODULE_ID } from "../asset/ids";
import { PHI_MEDIA_LIBRARY_ITEM_RENDERER_KEY } from "../../../constants/media-library-provider-keys";
import { PHI_DASHBOARD_CARD_ITEM_RENDERER_KEY } from "../../../constants/dashboard-card-provider-keys";
import { createPhiRuntimeModuleCalendarAdapterClientManifest } from "../../../components/runtime/runtime-module-calendar-adapter-client-manifest";
import { PHI_CORE_CALENDAR_ADAPTER_CLIENT_DEFINITIONS } from "../client-calendar-adapters/core";
import { PhiLazyAssetRuntimeControllerClient } from "../asset/client";
import {
  createPhiRuntimeModuleUiProviderClientManifest,
} from "../../../components/runtime/runtime-module-ui-provider-client-manifest";
import { PHI_AUTH_RUNTIME_MODULE_ID } from "../auth/ids";

export const PHI_COMMON_RUNTIME_MODULE_CALENDAR_ADAPTER_CLIENT_MANIFEST =
  createPhiRuntimeModuleCalendarAdapterClientManifest(
    PHI_CORE_CALENDAR_ADAPTER_CLIENT_DEFINITIONS,
  );

/**
 * The first-party Module UI providers, loaded where a node of their Module renders.
 *
 * The Auth and Asset providers carry their Modules' Form provider registries, and with them the Form
 * stack. Their Server catalogs only say that they have one (`uiProvider: true`).
 */
export const PHI_COMMON_RUNTIME_MODULE_UI_PROVIDER_CLIENT_MANIFEST =
  createPhiRuntimeModuleUiProviderClientManifest([
    {
      moduleId: PHI_AUTH_RUNTIME_MODULE_ID,
      Provider: dynamic(() =>
        import("../auth/form-ui-provider").then((module) => module.PhiAuthFormUiProvider)),
    },
    {
      moduleId: PHI_ASSET_RUNTIME_MODULE_ID,
      Provider: dynamic(() =>
        import("../asset/form-ui-provider").then((module) => module.PhiAssetFormUiProvider)),
    },
  ]);

export const PHI_COMMON_RUNTIME_MODULE_CONTROLLER_CLIENT_LOADERS = [
  [
    PHI_ASSET_RUNTIME_MODULE_ID,
    PhiLazyAssetRuntimeControllerClient,
  ],
] as const satisfies ReadonlyArray<readonly [string, PhiRuntimeModuleControllerClient]>;

export const PHI_COMMON_RUNTIME_MODULE_RENDER_CLIENT_MANIFEST =
  createPhiRuntimeModuleRenderClientManifest([
    [
      PhiRuntimeRenderClientType.RegionContainerEnhancer,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/regions/clients/cms-region-container-client").then((module) => module.PhiCmsRegionContainerClient)),
      ),
    ],
    [
      PhiRuntimeRenderClientType.OverlayContainer,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/overlays/phi-overlay-container-client").then((module) => module.PhiOverlayContainerClient)),
      ),
    ],
    [
      PhiRuntimeRenderClientType.SlotChildFrameEnhancer,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../runtime/phi-slot-child-frame-client").then((module) => module.PhiSlotChildFrameClient)),
      ),
    ],
    [
      // Behind the manifest like the descriptor it frames: imported by the Form Widget's Server
      // renderer, it was a client reference of every route -- the password-change prompt hangs under
      // every page -- and pulled the Form stack into the first load of pages without a Form.
      PhiRuntimeRenderClientType.FormWidgetFrame,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/forms/phi-form-widget-frame")
          .then((module) => module.PhiFormWidgetFrame)),
      ),
    ],
    [
      PhiRuntimeRenderClientType.FormDescriptor,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/forms/form-descriptor-runtime-client").then((module) => module.PhiFormDescriptorRuntimeClient)),
      ),
    ],
    [
      PhiCmsWidgetType.Button,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/button/client").then((module) => module.PhiButtonWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Cascader,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/cascader/client").then((module) => module.PhiCascaderWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Color,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/color/client").then((module) => module.PhiColorWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.DateInput,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/date-input/client").then((module) => module.PhiDateInputWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.CommandToolbar,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/command-toolbar/client").then((module) => module.PhiCommandToolbarWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Input,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/input/client").then((module) => module.PhiInputWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Otp,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/otp/client").then((module) => module.PhiOtpWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.NumberInput,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/number-input/client").then((module) => module.PhiNumberInputWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Slider,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/slider/client").then((module) => module.PhiSliderWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Rate,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/rate/client").then((module) => module.PhiRateWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Checkbox,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/checkbox/client").then((module) => module.PhiCheckboxWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.CheckboxGroup,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/checkbox-group/client").then((module) => module.PhiCheckboxGroupWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.MultiSelect,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/multi-select/client").then((module) => module.PhiMultiSelectWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Pagination,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/pagination/client").then((module) => module.PhiPaginationWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Segmented,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/segmented/client").then((module) => module.PhiSegmentedWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.SelectBox,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/select-box/client").then((module) => module.PhiSelectBoxWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Dimension,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/dimension/client").then((module) => module.PhiDimensionWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Length,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/length/client").then((module) => module.PhiLengthWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Gallery,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/gallery/client").then((module) => module.PhiGalleryWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Steps,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/steps/client").then((module) => module.PhiStepsWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Timeline,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/timeline/client").then((module) => module.PhiTimelineWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.FloatButton,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/float-button/client").then((module) => module.PhiFloatButtonWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Spacer,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/spacer/client").then((module) => module.PhiSpacerWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Switch,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/switch/client").then((module) => module.PhiSwitchWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.ThemeModeSwitch,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/theme-mode-switch/client").then((module) => module.PhiThemeModeSwitchWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Record,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/record/client").then((module) => module.PhiRecordWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.Table,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/table/client").then((module) => module.PhiTableWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.Tree,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/tree/client").then((module) => module.PhiTreeWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.Account,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/account/client").then((module) => module.PhiAccountWidgetClient)),
      ),
    ],
    [
      PhiRuntimeRenderClientType.AccountPreview,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/widgets/client/account-preview").then((module) => module.PhiAccountWidgetPreview)),
      ),
    ],
    [
      PhiCmsWidgetType.Breadcrumb,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/breadcrumb/client").then((module) => module.PhiBreadcrumbWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Card,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/widgets/shared/card-body-client").then((module) => module.PhiCardWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.CollectionView,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/collection-view/client").then((module) => module.PhiCollectionViewWidget)),
      ),
    ],
    // The Dashboard's card, on the same terms: a renderer the Collection View hosts by key. It is the
    // one that resolves each item's payload after the list, which is why the shared card View is not it.
    [
      PHI_DASHBOARD_CARD_ITEM_RENDERER_KEY,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../dashboard/clients/dashboard-card-view").then((module) => module.PhiDashboardCardCollectionViewBinding)),
      ),
    ],
    // An item renderer, not a Widget: the Collection View hosts it by the key its bound resource names,
    // or by the key a Site named instead. Nothing about this entry says it belongs to the Asset Module.
    [
      PHI_MEDIA_LIBRARY_ITEM_RENDERER_KEY,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/collection-view/asset-binding").then((module) => module.PhiAssetCollectionViewBinding)),
      ),
    ],
    [
      PhiCmsWidgetType.Description,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/description/client").then((module) => module.PhiDescriptionWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.DraftStatus,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/draft-status/client").then((module) => module.PhiDraftStatusWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.Html,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/html/client").then((module) => module.PhiHtmlWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.Icon,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/icon/client").then((module) => module.PhiIconWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.Image,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/image/client").then((module) => module.PhiImageWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.MarkdownToc,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/markdown-toc/client").then((module) => module.PhiMarkdownTocWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.PageTitle,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/page-title/client").then((module) => module.PhiPageTitleWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.VideoEmbed,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../video/widgets/embed/client").then((module) => module.PhiVideoEmbedWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.TourButton,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/tour-button/client").then((module) => module.PhiTourButtonWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.AccountAvatar,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../avatar/widgets/account-avatar/client").then((module) => module.PhiAccountAvatarWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.AccountAvatarPicker,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../avatar/widgets/account-avatar-picker/client").then((module) => module.PhiAccountAvatarPickerWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.EntryList,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/entry-list/client").then((module) => module.PhiEntryListWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.EntryDetail,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/entry-detail/client").then((module) => module.PhiEntryDetailWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.EntryHeader,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/entry-header/client").then((module) => module.PhiEntryHeaderWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.ThreadConversation,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/thread-conversation/client").then((module) => module.PhiThreadConversationWidgetClient)),
      ),
    ],
    [
      PhiRuntimeRenderClientType.SearchStandalone,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/widgets/client/search").then((module) => module.PhiSearchWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.SidebarNavigation,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/sidebar-navigation/client").then((module) => module.PhiSidebarNavigationWidgetClient)),
      ),
    ],
    [
      PhiRuntimeRenderClientType.SidebarNavigationPreview,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/sidebar-navigation/client").then((module) => module.PhiSidebarNavigationWidgetPreviewClient)),
      ),
    ],
    [
      PhiCmsWidgetType.TabBar,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/widgets/client/stack-tabs").then((module) => module.PhiTabBarWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.SlotUpload,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/slot-upload/client").then((module) => module.PhiSlotUploadWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.AreaUpload,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/media/phi-area-upload-widget").then((module) => module.PhiAreaUploadWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.ImageInspector,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/media/phi-image-inspector-widget").then((module) => module.PhiAssetConfigWidget)),
      ),
    ],
    [
      PhiCmsWidgetType.MediaPicker,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/media/phi-media-picker-widget").then((module) => module.PhiMediaPickerWidget)),
      ),
    ],
    [
      PhiCmsLayoutType.Grid,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/layouts/clients/phi-grid-layout-client").then((module) => module.PhiGridLayout)),
      ),
    ],
    [
      PhiCmsLayoutType.Collapsible,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/layouts/clients/phi-collapsible-layout-client").then((module) => module.PhiCollapsibleLayout)),
      ),
    ],
    [
      PhiCmsLayoutType.Stack,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/layouts/clients/phi-stack-layout-client").then((module) => module.PhiStackLayout)),
      ),
    ],
    [
      PhiCmsLayoutType.Carousel,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/layouts/clients/phi-carousel-layout-client").then((module) => module.PhiCarouselLayout)),
      ),
    ],
    [
      PhiCmsWidgetType.Brand,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/brand/client").then((module) => module.PhiBrandWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.HeaderNavigation,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/header-navigation/client").then((module) => module.PhiHeaderNavigationWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.Locale,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/shell/phi-locale-switch").then((module) => module.PhiLocaleSwitch)),
      ),
    ],
    [
      PhiCmsWidgetType.Markdown,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../../../components/widgets/shared/markdown-body-client").then((module) => module.PhiMarkdownWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.QuickLinks,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/quick-links/client").then((module) => module.PhiQuickLinksWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.SimpleText,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/simple-text/client").then((module) => module.PhiSimpleTextWidgetClient)),
      ),
    ],
    [
      PhiCmsWidgetType.FormPreview,
      definePhiRuntimeModuleRenderClient(
        dynamic(() => import("../core/widgets/form-preview/client").then((module) => module.PhiFormPreviewWidgetClient)),
      ),
    ],
  ]);
